import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { useProfile } from '@/modules/user-management/hooks/useAuth'
import { useSidebarGroups } from '@/modules/product-management/hooks/useProductTree'
import { useAppVersion, buildVersionTooltip } from '@/shared/hooks/useAppVersion'
import { AboutDialog } from '@/shared/components/ui'
import { APP_CONFIG } from '@/config/app'

interface SidebarProps {
  activeItem?: string
  collapsed?: boolean
  onCollapsedChange?: (collapsed: boolean) => void
}

// 文档链接配置
const DOC_LINKS: Record<string, string> = {
  // 普通用户文档
  '/dashboard': '/docs/core-features/dashboard',
  '/projects': '/docs/core-features/project-management',
  '/product-management': '/docs/core-features/project-management',
  '/detection-tasks': '/docs/core-features/task-creation',
  '/reports': '/docs/core-features/result-analysis',
  '/issues': '/docs/core-features/global-issue-list',
  '/feedback': '/docs/core-features/feedback',
  '/plugin-repo': '/docs/core-features/skill-repo',

  // 用户菜单文档
  '/profile': '/docs/personal-config/profile',
  '/api-keys': '/docs/personal-config/api-key-management',
  '/teams': '/docs/personal-config/team-management',
  '/user-management': '/docs/admin/user-management',
  '/oauth-providers': '/docs/admin/oauth-provider',

  // 管理员文档
  '/detection-config': '/docs/admin/detection-config',
  '/stream-detection': '/docs/admin/stream-detection',
  '/rule-validation': '/docs/admin/rule-validation',
  '/operations-management': '/docs/admin/system-monitor',
  '/admin/feedback': '/docs/admin/feedback-management',
  '/evaluation/overview': '/docs/admin/evaluation-overview',
  '/evaluation/samples': '/docs/admin/evaluation-samples',
  '/evaluation/datasets': '/docs/admin/evaluation-datasets',
  '/evaluation/runs': '/docs/admin/evaluation-runs',
  '/evaluation/comparison': '/docs/admin/evaluation-comparison',
  '/evaluation/rule-statistics': '/docs/admin/evaluation-statistics',
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed = false, onCollapsedChange }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { data: user, isLoading: userLoading } = useProfile()
  const { data: groups = [] } = useSidebarGroups()
  const appVersion = useAppVersion()
  const [showUserMenu, setShowUserMenu] = useState(false)
  // 左下角版本号点击后弹出的「关于」弹窗
  const [showAbout, setShowAbout] = useState(false)
  const [projectsExpanded, setProjectsExpanded] = useState(true)
  const [evaluationCollapsed, setEvaluationCollapsed] = useState<boolean>(APP_CONFIG.sidebar.evaluationCollapsed)
  const userMenuRef = useRef<HTMLDivElement>(null)

  const handleLogout = () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('loginTime')
    navigate('/login')
  }

  // 点击外部关闭弹窗
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserMenu])

  // 判断当前路由是否激活
  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const sidebarStyle = {
    position: 'fixed' as const,
    left: 0,
    top: 0,
    bottom: 0,
    width: collapsed ? '0px' : '260px',
    background: 'white',
    display: 'flex',
    flexDirection: 'column' as const,
    borderRight: '1px solid #e2e8f0',
    zIndex: 1000,
    transition: 'width 0.3s ease',
    overflow: 'hidden',
  }

  const sidebarHeaderStyle = {
    padding: '0.75rem 1.5rem',
    borderBottom: '1px solid #e2e8f0',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  }

  const collapseButtonStyle = {
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    background: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    color: '#64748b',
    transition: 'all 0.2s',
  }

  const collapseButtonHoverStyle = {
    background: '#f1f5f9',
    color: '#3b82f6',
    borderColor: '#3b82f6',
  }

  const brandLogoStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    textDecoration: 'none',
  }

  const brandLogoContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  }

  const sidebarNavStyle = {
    flex: 1,
    padding: '1.5rem 0',
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
  }

  const navSectionStyle = {
    marginBottom: '1.5rem',
  }

  const navSectionTitleStyle = {
    padding: '0 1.5rem',
    fontSize: '0.75rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    color: '#64748b',
    letterSpacing: '0.5px',
    marginBottom: '0.75rem',
  }

  const getNavItemStyle = (active: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1.5rem',
    color: active ? '#3b82f6' : '#64748b',
    textDecoration: 'none',
    fontWeight: active ? 600 : 500,
    transition: 'all 0.2s',
    cursor: 'pointer',
    borderLeft: `3px solid ${active ? '#3b82f6' : 'transparent'}`,
    background: active ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
    whiteSpace: 'nowrap' as const,
    flex: 1,
    minWidth: 0,
  })

  const navItemHoverStyle = {
    background: 'rgba(59, 130, 246, 0.05)',
    color: '#3b82f6',
  }

  const navIconStyle = {
    fontSize: '1.25rem',
    width: '24px',
    textAlign: 'center' as const,
  }

  const sidebarFooterStyle = {
    padding: '1.5rem',
    borderTop: '1px solid #e2e8f0',
    position: 'relative' as const,
    flexShrink: 0,
  }

  const userProfileStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.875rem',
    padding: '0.75rem',
    borderRadius: '10px',
    background: '#fafbfc',
    cursor: 'pointer',
    transition: 'all 0.2s',
  }

  const userProfileHoverStyle = {
    background: 'rgba(59, 130, 246, 0.05)',
  }

  const userAvatarStyle = {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    background: '#3b82f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 700,
    fontSize: '1.125rem',
  }

  const userInfoStyle = {
    flex: 1,
  }

  const userNameStyle = {
    fontWeight: 600,
    fontSize: '0.875rem',
    marginBottom: '0.125rem',
  }

  const userRoleStyle = {
    fontSize: '0.75rem',
    color: '#64748b',
  }

  // 部署版本号：用户信息卡片下方一行弱化小字，点击弹出「关于」弹窗（见 docs/version_management_design.md）
  const versionStyle = {
    marginTop: '0.5rem',
    paddingLeft: '0.75rem',
    fontSize: '0.7rem',
    color: '#94a3b8',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
  }

  const dropdownMenuStyle = {
    position: 'absolute' as const,
    bottom: '100%',
    left: '1.5rem',
    right: '1.5rem',
    marginBottom: '0.5rem',
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    zIndex: 1000,
  }

  const menuItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    color: '#475569',
    textDecoration: 'none',
    transition: 'all 0.2s',
    cursor: 'pointer',
    fontSize: '0.875rem',
  }

  const menuItemHoverStyle = {
    background: '#f1f5f9',
    color: '#3b82f6',
  }

  const menuDividerStyle = {
    height: '1px',
    background: '#e2e8f0',
    margin: '0.25rem 0',
  }

  // 帮助按钮样式
  const helpButtonStyle = {
    marginLeft: 'auto',
    padding: '0.25rem',
    borderRadius: '4px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: '#94a3b8',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
  }

  const navItemContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0',
    position: 'relative' as const,
    minWidth: 0,
    flex: 1,
    marginBottom: '0',
  }

  const navItemContainerHoverStyle = {
    background: 'rgba(59, 130, 246, 0.05)',
  }

  // 帮助按钮组件
  const HelpButton: React.FC<{ docUrl: string }> = ({ docUrl }) => (
    <button
      style={helpButtonStyle}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        window.open(docUrl, '_blank')
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.opacity = '1'
        e.currentTarget.style.color = '#3b82f6'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = '0'
        e.currentTarget.style.color = '#94a3b8'
      }}
      title="查看帮助文档"
    >
      📖
    </button>
  )

  // 带帮助按钮的菜单项组件
  const MenuItemWithHelp: React.FC<{
    to: string
    icon: string
    label: string
    onClick?: () => void
    /** 是否显示右侧的文档帮助按钮；「关于」这类没有对应指南的入口传 false */
    showHelp?: boolean
  }> = ({ to, icon, label, onClick, showHelp = true }) => {
    const docUrl = showHelp ? (DOC_LINKS[to] || '/docs/') : undefined
    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (onClick) onClick()
    }

    return (
      <Link
        to={to}
        style={{
          ...menuItemStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
        onMouseEnter={(e) => Object.assign(e.currentTarget.style, menuItemHoverStyle)}
        onMouseLeave={(e) => Object.assign(e.currentTarget.style, menuItemStyle)}
        onClick={handleClick}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span>{icon}</span>
          <span>{label}</span>
        </div>
        {docUrl && <HelpButton docUrl={docUrl} />}
      </Link>
    )
  }

  return (
    <>
      <aside style={sidebarStyle}>
        <div style={sidebarHeaderStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
            <Link to="/dashboard" style={brandLogoStyle}>
              <img
                src="/logo3.png"
                alt="AIDD Logo"
                style={{
                  height: '44px',
                  width: 'auto',
                }}
              />
              <div>
                <div style={{ fontSize: '1.375rem', fontWeight: '700', color: '#1e293b', letterSpacing: '-0.02em' }}>AIDD</div>
              </div>
            </Link>
            <button
              style={{
                padding: '0.25rem',
                borderRadius: '4px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: '0.875rem',
                color: '#94a3b8',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => window.open('/docs/', '_blank')}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '1'
                e.currentTarget.style.color = '#3b82f6'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '0.6'
                e.currentTarget.style.color = '#94a3b8'
              }}
              title="查看帮助文档"
            >
              📖
            </button>
          </div>
          <button
            style={collapseButtonStyle}
            onClick={() => onCollapsedChange?.(!collapsed)}
            onMouseEnter={(e) => Object.assign(e.currentTarget.style, collapseButtonHoverStyle)}
            onMouseLeave={(e) => Object.assign(e.currentTarget.style, collapseButtonStyle)}
            title="隐藏侧边栏"
          >
            ◀
          </button>
        </div>

      <nav style={sidebarNavStyle}>
        <div style={navSectionStyle}>
          <div style={navSectionTitleStyle}>核心功能</div>
          <div
            style={navItemContainerStyle}
            onMouseEnter={(e) => {
              const helpBtn = e.currentTarget.querySelector('button')
              if (helpBtn) (helpBtn as HTMLElement).style.opacity = '1'
            }}
            onMouseLeave={(e) => {
              const helpBtn = e.currentTarget.querySelector('button')
              if (helpBtn) (helpBtn as HTMLElement).style.opacity = '0'
            }}
          >
            <Link
              to="/dashboard"
              style={getNavItemStyle(isActive('/dashboard'))}
              onMouseEnter={(e) => {
                if (!isActive('/dashboard')) {
                  Object.assign(e.currentTarget.style, navItemHoverStyle)
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive('/dashboard')) {
                  Object.assign(e.currentTarget.style, getNavItemStyle(false))
                }
              }}
            >
              <span style={navIconStyle}>📊</span>
              <span>仪表盘</span>
            </Link>
            <HelpButton docUrl={DOC_LINKS['/dashboard']} />
          </div>
          {/* 项目管理 - 可展开一级菜单 + 二级领域菜单 */}
          <div style={{ ...navItemContainerStyle, flexDirection: 'column' as const, alignItems: 'stretch' }}>
            <div
              style={{ ...getNavItemStyle(isActive('/projects')), cursor: 'pointer' }}
              onClick={() => setProjectsExpanded((v) => !v)}
            >
              <span style={navIconStyle}>📁</span>
              <span>项目管理</span>
              <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: '#94a3b8' }}>
                {projectsExpanded ? '▼' : '▶'}
              </span>
            </div>
            {projectsExpanded && (
              <div>
                {groups.map((g) => {
                  const to = g.scope === 'domain' ? `/projects/domain/${g.id}` : `/projects/personal/${g.id}`
                  const a = isActive(to)
                  return (
                    <Link
                      key={`${g.scope}-${g.id}`}
                      to={to}
                      style={{
                        display: 'block',
                        padding: '0.5rem 1.5rem 0.5rem 2.75rem',
                        color: a ? '#2563eb' : '#475569',
                        background: a ? '#eff6ff' : 'transparent',
                        textDecoration: 'none',
                        fontWeight: a ? 600 : 500,
                        borderLeft: `3px solid ${a ? '#3b82f6' : 'transparent'}`,
                      }}
                    >
                      {g.name}
                    </Link>
                  )
                })}
              </div>
            )}
          </div>
          <div style={navItemContainerStyle}>
            <Link
              to="/projects-list"
              style={getNavItemStyle(isActive('/projects-list'))}
              onMouseEnter={(e) => {
                if (!isActive('/projects-list')) {
                  Object.assign(e.currentTarget.style, navItemHoverStyle)
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive('/projects-list')) {
                  Object.assign(e.currentTarget.style, getNavItemStyle(false))
                }
              }}
            >
              <span style={navIconStyle}>🗂️</span>
              <span>项目管理（旧）</span>
            </Link>
          </div>
          <div
            style={navItemContainerStyle}
            onMouseEnter={(e) => {
              const helpBtn = e.currentTarget.querySelector('button')
              if (helpBtn) (helpBtn as HTMLElement).style.opacity = '1'
            }}
            onMouseLeave={(e) => {
              const helpBtn = e.currentTarget.querySelector('button')
              if (helpBtn) (helpBtn as HTMLElement).style.opacity = '0'
            }}
          >
            <Link
              to="/detection-tasks"
              style={getNavItemStyle(isActive('/detection-tasks'))}
              onMouseEnter={(e) => {
                if (!isActive('/detection-tasks')) {
                  Object.assign(e.currentTarget.style, navItemHoverStyle)
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive('/detection-tasks')) {
                  Object.assign(e.currentTarget.style, getNavItemStyle(false))
                }
              }}
            >
              <span style={navIconStyle}>✅</span>
              <span>检测任务</span>
            </Link>
            <HelpButton docUrl={DOC_LINKS['/detection-tasks']} />
          </div>
          <div
            style={navItemContainerStyle}
            onMouseEnter={(e) => {
              const helpBtn = e.currentTarget.querySelector('button')
              if (helpBtn) (helpBtn as HTMLElement).style.opacity = '1'
            }}
            onMouseLeave={(e) => {
              const helpBtn = e.currentTarget.querySelector('button')
              if (helpBtn) (helpBtn as HTMLElement).style.opacity = '0'
            }}
          >
            <Link
              to="/reports"
              style={getNavItemStyle(isActive('/reports'))}
              onMouseEnter={(e) => {
                if (!isActive('/reports')) {
                  Object.assign(e.currentTarget.style, navItemHoverStyle)
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive('/reports')) {
                  Object.assign(e.currentTarget.style, getNavItemStyle(false))
                }
              }}
            >
              <span style={navIconStyle}>📋</span>
              <span>问题报告</span>
            </Link>
            <HelpButton docUrl={DOC_LINKS['/reports']} />
          </div>
          <div
            style={navItemContainerStyle}
            onMouseEnter={(e) => {
              const helpBtn = e.currentTarget.querySelector('button')
              if (helpBtn) (helpBtn as HTMLElement).style.opacity = '1'
            }}
            onMouseLeave={(e) => {
              const helpBtn = e.currentTarget.querySelector('button')
              if (helpBtn) (helpBtn as HTMLElement).style.opacity = '0'
            }}
          >
            <Link
              to="/issues"
              style={getNavItemStyle(isActive('/issues'))}
              onMouseEnter={(e) => {
                if (!isActive('/issues')) {
                  Object.assign(e.currentTarget.style, navItemHoverStyle)
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive('/issues')) {
                  Object.assign(e.currentTarget.style, getNavItemStyle(false))
                }
              }}
            >
              <span style={navIconStyle}>🐛</span>
              <span>问题列表</span>
            </Link>
            <HelpButton docUrl={DOC_LINKS['/issues']} />
          </div>
          <div
            style={navItemContainerStyle}
            onMouseEnter={(e) => {
              const helpBtn = e.currentTarget.querySelector('button')
              if (helpBtn) (helpBtn as HTMLElement).style.opacity = '1'
            }}
            onMouseLeave={(e) => {
              const helpBtn = e.currentTarget.querySelector('button')
              if (helpBtn) (helpBtn as HTMLElement).style.opacity = '0'
            }}
          >
            <Link
              to="/feedback"
              style={getNavItemStyle(isActive('/feedback'))}
              onMouseEnter={(e) => {
                if (!isActive('/feedback')) {
                  Object.assign(e.currentTarget.style, navItemHoverStyle)
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive('/feedback')) {
                  Object.assign(e.currentTarget.style, getNavItemStyle(false))
                }
              }}
            >
              <span style={navIconStyle}>💬</span>
              <span>反馈与建议</span>
            </Link>
            <HelpButton docUrl={DOC_LINKS['/feedback']} />
          </div>
          {/* 插件仓库 - 所有用户可见 */}
          <div
            style={navItemContainerStyle}
            onMouseEnter={(e) => {
              const helpBtn = e.currentTarget.querySelector('button')
              if (helpBtn) (helpBtn as HTMLElement).style.opacity = '1'
            }}
            onMouseLeave={(e) => {
              const helpBtn = e.currentTarget.querySelector('button')
              if (helpBtn) (helpBtn as HTMLElement).style.opacity = '0'
            }}
          >
            <Link
              to="/plugin-repo"
              style={getNavItemStyle(isActive('/plugin-repo'))}
              onMouseEnter={(e) => {
                if (!isActive('/plugin-repo')) {
                  Object.assign(e.currentTarget.style, navItemHoverStyle)
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive('/plugin-repo')) {
                  Object.assign(e.currentTarget.style, getNavItemStyle(false))
                }
              }}
            >
              <span style={navIconStyle}>📦</span>
              <span>插件仓库</span>
            </Link>
            <HelpButton docUrl={DOC_LINKS['/plugin-repo']} />
          </div>
        </div>

        {/* 系统管理 */}
        <div style={navSectionStyle}>
          <div style={navSectionTitleStyle}>系统管理</div>
          <div
            style={navItemContainerStyle}
            onMouseEnter={(e) => {
              const helpBtn = e.currentTarget.querySelector('button')
              if (helpBtn) (helpBtn as HTMLElement).style.opacity = '1'
            }}
            onMouseLeave={(e) => {
              const helpBtn = e.currentTarget.querySelector('button')
              if (helpBtn) (helpBtn as HTMLElement).style.opacity = '0'
            }}
          >
            <Link
              to="/product-management"
              style={getNavItemStyle(isActive('/product-management'))}
              onMouseEnter={(e) => {
                if (!isActive('/product-management')) {
                  Object.assign(e.currentTarget.style, navItemHoverStyle)
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive('/product-management')) {
                  Object.assign(e.currentTarget.style, getNavItemStyle(false))
                }
              }}
            >
              <span style={navIconStyle}>📦</span>
              <span>产品管理</span>
            </Link>
            <HelpButton docUrl={DOC_LINKS['/product-management']} />
          </div>
          {/* 检测配置 - 所有用户可见 */}
          <div
            style={navItemContainerStyle}
            onMouseEnter={(e) => {
              const helpBtn = e.currentTarget.querySelector('button')
              if (helpBtn) (helpBtn as HTMLElement).style.opacity = '1'
            }}
            onMouseLeave={(e) => {
              const helpBtn = e.currentTarget.querySelector('button')
              if (helpBtn) (helpBtn as HTMLElement).style.opacity = '0'
            }}
          >
            <Link
              to="/detection-config"
              style={getNavItemStyle(isActive('/detection-config'))}
              onMouseEnter={(e) => {
                if (!isActive('/detection-config')) {
                  Object.assign(e.currentTarget.style, navItemHoverStyle)
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive('/detection-config')) {
                  Object.assign(e.currentTarget.style, getNavItemStyle(false))
                }
              }}
            >
              <span style={navIconStyle}>⚙️</span>
              <span>检测配置</span>
            </Link>
            <HelpButton docUrl={DOC_LINKS['/detection-config']} />
          </div>
          {/* 运维管理项 - 仅管理员可见 */}
          {user?.role === 'ADMIN' && (
            <>
              <div
                style={navItemContainerStyle}
                onMouseEnter={(e) => {
                  const helpBtn = e.currentTarget.querySelector('button')
                  if (helpBtn) (helpBtn as HTMLElement).style.opacity = '1'
                }}
                onMouseLeave={(e) => {
                  const helpBtn = e.currentTarget.querySelector('button')
                  if (helpBtn) (helpBtn as HTMLElement).style.opacity = '0'
                }}
              >
                <Link
                  to="/operations-management"
                  style={getNavItemStyle(isActive('/operations-management'))}
                  onMouseEnter={(e) => {
                    if (!isActive('/operations-management')) {
                      Object.assign(e.currentTarget.style, navItemHoverStyle)
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive('/operations-management')) {
                      Object.assign(e.currentTarget.style, getNavItemStyle(false))
                    }
                  }}
                >
                  <span style={navIconStyle}>📈</span>
                  <span>系统监控</span>
                </Link>
                <HelpButton docUrl={DOC_LINKS['/operations-management']} />
              </div>
              <div
                style={navItemContainerStyle}
                onMouseEnter={(e) => {
                  const helpBtn = e.currentTarget.querySelector('button')
                  if (helpBtn) (helpBtn as HTMLElement).style.opacity = '1'
                }}
                onMouseLeave={(e) => {
                  const helpBtn = e.currentTarget.querySelector('button')
                  if (helpBtn) (helpBtn as HTMLElement).style.opacity = '0'
                }}
              >
                <Link
                  to="/admin/feedback"
                  style={getNavItemStyle(isActive('/admin/feedback'))}
                  onMouseEnter={(e) => {
                    if (!isActive('/admin/feedback')) {
                      Object.assign(e.currentTarget.style, navItemHoverStyle)
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive('/admin/feedback')) {
                      Object.assign(e.currentTarget.style, getNavItemStyle(false))
                    }
                  }}
                >
                  <span style={navIconStyle}>💼</span>
                  <span>反馈管理</span>
                </Link>
                <HelpButton docUrl={DOC_LINKS['/admin/feedback']} />
              </div>
            </>
          )}
        </div>

        {/* 评测管理 - 仅管理员可见，可折叠 */}
        {user?.role === 'ADMIN' && (
          <div style={navSectionStyle}>
            <div
              style={{ ...navSectionTitleStyle, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              onClick={() => setEvaluationCollapsed(!evaluationCollapsed)}
            >
              <span>评测管理</span>
              <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{evaluationCollapsed ? '▶' : '▼'}</span>
            </div>
            {!evaluationCollapsed && (
              <>
            <div
              style={navItemContainerStyle}
              onMouseEnter={(e) => {
                const helpBtn = e.currentTarget.querySelector('button')
                if (helpBtn) (helpBtn as HTMLElement).style.opacity = '1'
              }}
              onMouseLeave={(e) => {
                const helpBtn = e.currentTarget.querySelector('button')
                if (helpBtn) (helpBtn as HTMLElement).style.opacity = '0'
              }}
            >
              <Link
                to="/evaluation/overview"
                style={getNavItemStyle(isActive('/evaluation/overview'))}
                onMouseEnter={(e) => {
                  if (!isActive('/evaluation/overview')) {
                    Object.assign(e.currentTarget.style, navItemHoverStyle)
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive('/evaluation/overview')) {
                    Object.assign(e.currentTarget.style, getNavItemStyle(false))
                  }
                }}
              >
                <span style={navIconStyle}>📊</span>
                <span>评估概览</span>
              </Link>
              <HelpButton docUrl={DOC_LINKS['/evaluation/overview']} />
            </div>
            <div
              style={navItemContainerStyle}
              onMouseEnter={(e) => {
                const helpBtn = e.currentTarget.querySelector('button')
                if (helpBtn) (helpBtn as HTMLElement).style.opacity = '1'
              }}
              onMouseLeave={(e) => {
                const helpBtn = e.currentTarget.querySelector('button')
                if (helpBtn) (helpBtn as HTMLElement).style.opacity = '0'
              }}
            >
              <Link
                to="/evaluation/samples"
                style={getNavItemStyle(isActive('/evaluation/samples'))}
                onMouseEnter={(e) => {
                  if (!isActive('/evaluation/samples')) {
                    Object.assign(e.currentTarget.style, navItemHoverStyle)
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive('/evaluation/samples')) {
                    Object.assign(e.currentTarget.style, getNavItemStyle(false))
                  }
                }}
              >
                <span style={navIconStyle}>📄</span>
                <span>测试样本</span>
              </Link>
              <HelpButton docUrl={DOC_LINKS['/evaluation/samples']} />
            </div>
            <div
              style={navItemContainerStyle}
              onMouseEnter={(e) => {
                const helpBtn = e.currentTarget.querySelector('button')
                if (helpBtn) (helpBtn as HTMLElement).style.opacity = '1'
              }}
              onMouseLeave={(e) => {
                const helpBtn = e.currentTarget.querySelector('button')
                if (helpBtn) (helpBtn as HTMLElement).style.opacity = '0'
              }}
            >
              <Link
                to="/evaluation/datasets"
                style={getNavItemStyle(isActive('/evaluation/datasets'))}
                onMouseEnter={(e) => {
                  if (!isActive('/evaluation/datasets')) {
                    Object.assign(e.currentTarget.style, navItemHoverStyle)
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive('/evaluation/datasets')) {
                    Object.assign(e.currentTarget.style, getNavItemStyle(false))
                  }
                }}
              >
                <span style={navIconStyle}>📚</span>
                <span>数据集</span>
              </Link>
              <HelpButton docUrl={DOC_LINKS['/evaluation/datasets']} />
            </div>
            <div
              style={navItemContainerStyle}
              onMouseEnter={(e) => {
                const helpBtn = e.currentTarget.querySelector('button')
                if (helpBtn) (helpBtn as HTMLElement).style.opacity = '1'
              }}
              onMouseLeave={(e) => {
                const helpBtn = e.currentTarget.querySelector('button')
                if (helpBtn) (helpBtn as HTMLElement).style.opacity = '0'
              }}
            >
              <Link
                to="/evaluation/runs"
                style={getNavItemStyle(isActive('/evaluation/runs'))}
                onMouseEnter={(e) => {
                  if (!isActive('/evaluation/runs')) {
                    Object.assign(e.currentTarget.style, navItemHoverStyle)
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive('/evaluation/runs')) {
                    Object.assign(e.currentTarget.style, getNavItemStyle(false))
                  }
                }}
              >
                <span style={navIconStyle}>🚀</span>
                <span>评测运行</span>
              </Link>
              <HelpButton docUrl={DOC_LINKS['/evaluation/runs']} />
            </div>
            <div
              style={navItemContainerStyle}
              onMouseEnter={(e) => {
                const helpBtn = e.currentTarget.querySelector('button')
                if (helpBtn) (helpBtn as HTMLElement).style.opacity = '1'
              }}
              onMouseLeave={(e) => {
                const helpBtn = e.currentTarget.querySelector('button')
                if (helpBtn) (helpBtn as HTMLElement).style.opacity = '0'
              }}
            >
              <Link
                to="/evaluation/comparison"
                style={getNavItemStyle(isActive('/evaluation/comparison'))}
                onMouseEnter={(e) => {
                  if (!isActive('/evaluation/comparison')) {
                    Object.assign(e.currentTarget.style, navItemHoverStyle)
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive('/evaluation/comparison')) {
                    Object.assign(e.currentTarget.style, getNavItemStyle(false))
                  }
                }}
              >
                <span style={navIconStyle}>🔍</span>
                <span>对比分析</span>
              </Link>
              <HelpButton docUrl={DOC_LINKS['/evaluation/comparison']} />
            </div>
            <div
              style={navItemContainerStyle}
              onMouseEnter={(e) => {
                const helpBtn = e.currentTarget.querySelector('button')
                if (helpBtn) (helpBtn as HTMLElement).style.opacity = '1'
              }}
              onMouseLeave={(e) => {
                const helpBtn = e.currentTarget.querySelector('button')
                if (helpBtn) (helpBtn as HTMLElement).style.opacity = '0'
              }}
            >
              <Link
                to="/evaluation/rule-statistics"
                style={getNavItemStyle(isActive('/evaluation/rule-statistics'))}
                onMouseEnter={(e) => {
                  if (!isActive('/evaluation/rule-statistics')) {
                    Object.assign(e.currentTarget.style, navItemHoverStyle)
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive('/evaluation/rule-statistics')) {
                    Object.assign(e.currentTarget.style, getNavItemStyle(false))
                  }
                }}
              >
                <span style={navIconStyle}>📈</span>
                <span>规则统计</span>
              </Link>
              <HelpButton docUrl={DOC_LINKS['/evaluation/rule-statistics']} />
            </div>
            </>
            )}
          </div>
        )}

        {/* 系统工具 - 暂时隐藏
        <div style={navSectionStyle}>
          <div style={navSectionTitleStyle}>系统工具</div>
          <Link
            to="/system-monitor"
            style={getNavItemStyle(isActive('/system-monitor'))}
            onMouseEnter={(e) => {
              if (!isActive('/system-monitor')) {
                Object.assign(e.currentTarget.style, navItemHoverStyle)
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive('/system-monitor')) {
                Object.assign(e.currentTarget.style, getNavItemStyle(false))
              }
            }}
          >
            <span style={navIconStyle}>📈</span>
            <span>系统监控</span>
          </Link>
          <Link
            to="/notifications"
            style={getNavItemStyle(isActive('/notifications'))}
            onMouseEnter={(e) => {
              if (!isActive('/notifications')) {
                Object.assign(e.currentTarget.style, navItemHoverStyle)
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive('/notifications')) {
                Object.assign(e.currentTarget.style, getNavItemStyle(false))
              }
            }}
          >
            <span style={navIconStyle}>🔔</span>
            <span>通知中心</span>
          </Link>
        </div>
        */}
      </nav>

      <div style={sidebarFooterStyle} ref={userMenuRef}>
        {/* 用户下拉菜单 */}
        {showUserMenu && (
          <div style={dropdownMenuStyle}>
            <MenuItemWithHelp
              to="/profile"
              icon="👤"
              label="个人资料"
              onClick={() => setShowUserMenu(false)}
            />

            <MenuItemWithHelp
              to="/api-keys"
              icon="🔑"
              label="API密钥"
              onClick={() => setShowUserMenu(false)}
            />

            <MenuItemWithHelp
              to="/teams"
              icon="👥"
              label="团队管理"
              onClick={() => setShowUserMenu(false)}
            />

            {user?.role === 'ADMIN' && (
              <>
                <div style={menuDividerStyle} />
                <MenuItemWithHelp
                  to="/user-management"
                  icon="👨‍👩‍👧‍👦"
                  label="用户列表"
                  onClick={() => setShowUserMenu(false)}
                />
                <MenuItemWithHelp
                  to="/oauth-providers"
                  icon="🔐"
                  label="OAuth提供商"
                  onClick={() => setShowUserMenu(false)}
                />
              </>
            )}

            <div style={menuDividerStyle} />

            {/* 关于：所有登录用户可见，跳转到 /about 展示系统与部署版本信息 */}
            <MenuItemWithHelp
              to="/about"
              icon="ℹ️"
              label="关于"
              showHelp={false}
              onClick={() => setShowUserMenu(false)}
            />

            <div
              style={menuItemStyle}
              onMouseEnter={(e) => Object.assign(e.currentTarget.style, menuItemHoverStyle)}
              onMouseLeave={(e) => Object.assign(e.currentTarget.style, menuItemStyle)}
              onClick={() => {
                setShowUserMenu(false)
                handleLogout()
              }}
            >
              <span>🚪</span>
              <span>退出登录</span>
            </div>
          </div>
        )}

        {/* 用户信息卡片 */}
        <div
          style={userProfileStyle}
          onClick={() => setShowUserMenu(!showUserMenu)}
          onMouseEnter={(e) => Object.assign(e.currentTarget.style, userProfileHoverStyle)}
          onMouseLeave={(e) => Object.assign(e.currentTarget.style, userProfileStyle)}
        >
          <div style={userAvatarStyle}>
            {userLoading ? '...' : (user?.display_name?.charAt(0).toUpperCase() || user?.username?.charAt(0).toUpperCase() || '?')}
          </div>
          <div style={userInfoStyle}>
            <div style={userNameStyle}>
              {userLoading ? '加载中...' : (user?.display_name || user?.username || '未登录')}
            </div>
            <div style={userRoleStyle}>
              {userLoading ? '' : (user?.role === 'ADMIN' ? '系统管理员' : user?.role === 'USER' ? '普通用户' : user?.username || '')}
              {user?.is_vip && ' · VIP'}
            </div>
          </div>
          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            {showUserMenu ? '▼' : '▲'}
          </span>
        </div>

        {/* 部署版本号：点击弹出「关于」弹窗 */}
        <div
          style={versionStyle}
          title={buildVersionTooltip(appVersion) || '查看版本详情'}
          onClick={() => setShowAbout(true)}
        >
          版本 {appVersion.version}
        </div>
      </div>

      {/* 关于弹窗（入口：上方版本号） */}
      <AboutDialog isOpen={showAbout} onClose={() => setShowAbout(false)} />
    </aside>

    {/* 展开按钮（侧边栏隐藏时显示） */}
    {collapsed && (
      <div
        style={{
          position: 'fixed',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 999
        }}
      >
        <button
          onClick={() => onCollapsedChange?.(false)}
          style={{
            border: 'none',
            borderRadius: '0 8px 8px 0',
            padding: '16px 8px',
            background: '#3b82f6',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#2563eb'
            e.currentTarget.style.paddingLeft = '12px'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#3b82f6'
            e.currentTarget.style.paddingLeft = '8px'
          }}
          title="展开侧边栏"
        >
          <span style={{ writingMode: 'vertical-lr', fontSize: 14, fontWeight: 600 }}>
            展开菜单
          </span>
        </button>
      </div>
    )}
  </>
  )
}

export default Sidebar
